import React from 'react';
import { PeriodType } from '../types/prompt/PeriodType';
import { MonitorType } from '../types/monitor/MonitorType';
import { ChatType, projectChatType } from '../types/monitor/ChatType';
import { AccountResponse } from '../models/account/AccountResponse';
import { LinksData } from '../../pages/prompt/preview/components/stats/models/StatsModel';

export const getPeriod = (period?: string | ''): PeriodType => {
  if (period?.toUpperCase() === 'WEEKLY') {
    return 'WEEKLY';
  } else if (period?.toUpperCase() === 'MONTHLY') {
    return 'MONTHLY';
  } else {
    return 'DAILY';
  }
};

export const getPeriodUrl = (periodType: PeriodType): string => {
  switch (periodType) {
    case 'DAILY':
    case 'WEEKLY':
    case 'MONTHLY':
      return periodType.toLowerCase();

    default:
      return 'DAILY'.toLowerCase();
  }
};

export const prepareAndGetChatType = (
  accountResponse: AccountResponse | null,
  defaultValue: ChatType,
  chat?: string
): ChatType => {
  if (!chat) {
    return defaultValue;
  }

  const chatType = chat.toUpperCase();
  if (projectChatType(accountResponse).includes(chatType)) {
    if (chatType === 'GEMINI') {
      return 'GEMINI';
    } else if (chatType === 'GPT') {
      return 'GPT';
    } else if (chatType === 'GPT_BROWSER') {
      return 'GPT_BROWSER';
    } else if (chatType === 'GPT_SEARCH') {
      return 'GPT_SEARCH';
    } else if (chatType === 'CLAUDE') {
      return 'CLAUDE';
    } else if (chatType === 'DEEP_SEEK') {
      return 'DEEP_SEEK';
    } else if (chatType === 'PERPLEXITY') {
      return 'PERPLEXITY';
    } else if (chatType === 'PERPLEXITY_BROWSER') {
      return 'PERPLEXITY_BROWSER';
    } else if (chatType === 'GROK') {
      return 'GROK';
    } else if (chatType === 'GROK_BROWSER') {
      return 'GROK_BROWSER';
    } else if (chatType === 'AI_OVERVIEW_BROWSER') {
      return 'AI_OVERVIEW_BROWSER';
    } else if (chatType === 'AI_MODE_BROWSER') {
      return 'AI_MODE_BROWSER';
    } else if (chatType === 'AI_OVERVIEW_NEW_BROWSER') {
      return 'AI_OVERVIEW_NEW_BROWSER';
    } else if (chatType === 'COPILOT_BROWSER') {
      return 'COPILOT_BROWSER';
    } else if (chatType === 'GEMINI_BROWSER') {
      return 'GEMINI_BROWSER';
    }
  }

  return defaultValue;
};

export const getMonitorTypeByChatOptionsType = (chatType: ChatType): MonitorType => {
  switch (chatType) {
    case 'GPT':
      return 'GPT';

    case 'GPT_SEARCH':
      return 'GPT_API_SEARCH_WEB';

    case 'GPT_BROWSER':
      return 'GPT_SEARCH_BROWSER';

    case 'CLAUDE':
      return 'CLAUDE_API';

    case 'GEMINI':
      return 'GEMINI_API';

    case 'DEEP_SEEK':
      return 'DEEP_SEEK_API';

    case 'PERPLEXITY':
      return 'PERPLEXITY_API';

    case 'PERPLEXITY_BROWSER':
      return 'PERPLEXITY_SEARCH_BROWSER';

    case 'GROK':
      return 'GROK_API';

    case 'GROK_BROWSER':
      return 'GROK_SEARCH_BROWSER';

    case 'AI_OVERVIEW_BROWSER':
      return 'AI_OVERVIEW';

    case 'AI_MODE_BROWSER':
      return 'AI_MODE';

    case 'AI_OVERVIEW_NEW_BROWSER':
      return 'AI_OVERVIEW_NEW';

    case 'COPILOT_BROWSER':
      return 'COPILOT_SEARCH_BROWSER';

    case 'GEMINI_BROWSER':
      return 'GEMINI_SEARCH_BROWSER';

    default:
      return 'ALL';
  }
};

export const getStatsUrl = (monitorType: MonitorType | null, periodType: PeriodType | null) => {
  const firstPart = monitorType === 'GPT_API' ? 'api' : 'browser';
  return `${firstPart}/${periodType?.toLowerCase()}`;
};

export const calculatePercentageShare = (
  count: number,
  allSourcesCount: number,
  addPercentChar = true
) => {
  const character = addPercentChar ? '%' : '';
  if (allSourcesCount === 0 || count === 0) {
    return `0${character}`;
  }

  const percentage = (count / allSourcesCount) * 100;

  const minValue = 0.1;
  if (percentage > 0 && percentage < minValue) {
    return `${minValue}${character}`;
  }

  return `${parseFloat(percentage.toFixed(1))}${character}`;
};

export const getAllSourcesCount = (sources: LinksData[]) =>
  sources.reduce((sum, val) => sum + val.count, 0);

/**
 * Formats share percentage with one decimal place
 */
export const formatShare = (count: number, total: number): string => {
  return total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
};

/**
 * Formats share change with appropriate styling classes
 */
export const formatShareChange = (
  shareChange: number,
  positiveClass: string,
  negativeClass: string,
  neutralClass?: string
): React.ReactElement => {
  const isZero = shareChange === 0;
  const isPositive = shareChange > 0;
  // Always show icon, use chart-up for zero
  const iconClass = isZero ? 'ico ico--chart-up' : (isPositive ? 'ico ico--chart-up' : 'ico ico--chart-down');
  const sign = isZero ? '' : (isPositive ? '+' : '');
  const value = Math.abs(shareChange).toFixed(2);

  // Extract base class name (e.g., 'sources-stats-table__change-positive' -> 'sources-stats-table__change')
  const baseClassName = positiveClass.replace(/-positive$/, '').replace(/-negative$/, '');
  const changeClassName = baseClassName.includes('__change')
    ? baseClassName
    : `${baseClassName}__change`;
  
  // Use neutral class if zero, otherwise use positive/negative
  const colorClassName = isZero && neutralClass
    ? neutralClass
    : (isPositive ? positiveClass : negativeClass);

  return React.createElement(
    'span',
    { className: `${changeClassName} ${colorClassName}` },
    React.createElement('span', { className: iconClass }),
    `${sign}${value}%`
  );
};

/**
 * Formats count change (absolute numeric difference) with appropriate styling classes
 */
export const formatCountChange = (
  countChange: number,
  positiveClass: string,
  negativeClass: string,
  neutralClass?: string
): React.ReactElement => {
  const isZero = countChange === 0;
  const isPositive = countChange > 0;
  // Always show icon, use chart-up for zero
  const iconClass = isZero ? 'ico ico--chart-up' : (isPositive ? 'ico ico--chart-up' : 'ico ico--chart-down');
  const sign = isZero ? '' : (isPositive ? '+' : '');
  const value = Math.abs(countChange);

  // Extract base class name (e.g., 'sources-stats-table__change-positive' -> 'sources-stats-table__change')
  const baseClassName = positiveClass.replace(/-positive$/, '').replace(/-negative$/, '');
  const changeClassName = baseClassName.includes('__change')
    ? baseClassName
    : `${baseClassName}__change`;
  
  // Use neutral class if zero, otherwise use positive/negative
  const colorClassName = isZero && neutralClass
    ? neutralClass
    : (isPositive ? positiveClass : negativeClass);

  return React.createElement(
    'span',
    { className: `${changeClassName} ${colorClassName}` },
    React.createElement('span', { className: iconClass }),
    `${sign}${value}`
  );
};
